import { graphql } from "gatsby";
import { Link } from "gatsby";
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import Layout from "../layouts/default";
import BGImageHero from "../sections/BGImageHero";
import { Colors } from "../components/styles/Colors";
import Chevron from "../components/Chevron";
import { BodyStyles, Header3, Body, Header5Styles } from "../components/typography";
import Breakpoints from "../components/styles/Breakpoints";
import richText from "../utils/richText";
import slugify from 'slugify'
import { PrimaryButton } from "../components/Button";
import { gsap, ScrollToPlugin, ScrollTrigger } from 'gsap/all'
import { EMAIL } from "../utils/constants";

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger)

export const query = graphql`
  {
    contentfulFaqPage {
      backgroundImage {
        fluid(maxWidth: 1440, quality: 100) {
          ...GatsbyContentfulFluid_withWebp
        }
      }
      faqs {
        category
        enabled
        answer {
          raw
        }
        question {
          question
        }
      }
    }
  }
`;

const Wrapper = styled.div`
  display: flex;
  width: 90%;
  margin: 100px auto;

  @media ${Breakpoints.desktop} {
    width: 70%;
  }
`;

const StickyNav = styled.div`
  position: sticky;
  top: 150px;
  width: 350px;
  align-self: flex-start;
  height: auto;
  display: none;

  @media ${Breakpoints.desktop} {
    display: block;
  }
  
  button {
    display: block;
    background: none;
    outline: 0;
    border: 0;
    box-shadow: none;
    padding: 15px 0px;
    width: 80%;
    border-bottom: 1px solid ${Colors.webGrey};
    text-align: left;
    ${BodyStyles};
    color: ${Colors.webGrey};
    transition: color .3s;

    &.active {
      color: ${Colors.darkGrey};
    }
  }
`;

const FaqsWrapper = styled.div`
  width: 100%;

  ${Header3} {
    color: ${Colors.hunter};
    margin-bottom: 30px;
  }

  .items {
    margin-bottom: 80px;
  }
`;

const FaqBody = styled(Body)``

const AccordionSection = styled.div`
  padding: 25px 0;
  width: 100%;

  border-bottom: 1px solid ${Colors.webGrey};

  &:last-of-type {
    border-bottom: 0;
  }

  > ${Body} {
    display: block;
    margin-top: 24px;

    p ~ p {
      margin-top: 15px;
    }
  }

  ${FaqBody} {
    display: none;
  }

  &.active {
    ${FaqBody} {
      display: block;
    }

    svg {
      transform: rotate(180deg);
    }
  }
`;

const AccordionHeader = styled.button`
  ${Header5Styles}

  display: flex;
  text-align: left;

  justify-content: space-between;
  align-items: center;

  width: 100%;
  height: 32px;

  padding: 0;

  outline: none;

  color: ${Colors.grey};

  background: 0;
  border: 0;
`;

const Callout = styled.div`
  background-color: ${Colors.ochreDark};
  padding: 30px;
  align-items: center;

  @media ${Breakpoints.desktop} {
    display: flex;
    padding: 50px;
  }

  p {
    width: 100%;
    ${BodyStyles};
    color: ${Colors.cream1};
    padding-right: 20px;
    margin-bottom: 15px;

    @media ${Breakpoints.desktop} {
      margin-bottom: 0px;
    }
  }

  ${PrimaryButton} {
    background-color: white;
    color: ${Colors.hunter};
    width: 100%;

    @media ${Breakpoints.desktop} {
      width: 125px;
    }
  }
`;

function FaqItem({ item }) {
  const [open, setOpen] = useState(false)

  return (
    <AccordionSection
      className={open ? 'active' : null}
    >
      <AccordionHeader
        onClick={() => setOpen(!open)}
      >
        <span>{item.question.question}</span> <Chevron />
      </AccordionHeader>
      <FaqBody>{richText(item.answer.raw)}</FaqBody>
    </AccordionSection>
  )
}

function FaqSection({ cat, faqPage, index, setActiveCategory }) {
  const id = `${slugify(cat, { lower: true })}-section`

  useEffect(() => {
    ScrollTrigger.create({
      trigger: `#${id}`,
      start: "top 50%",
      end: "bottom 50%",
      onEnter: () => {
        setActiveCategory(index)
      },
      onEnterBack: () => {
        setActiveCategory(index)
      }
    })
  })

  return (
    <div id={id} className="category-section">
      <Header3>{cat}</Header3>

      <div className="items">
        {faqPage.faqs.filter(faq => faq.enabled && faq.category === cat).map((item, index) => (
          <FaqItem item={item} key={index}></FaqItem>
        ))}
      </div>
    </div>
  )
}

export default function Faq({ data, location }) {
  const faqPage = data.contentfulFaqPage;
  const categories = []
  const [activeCategory, setActiveCategory] = useState(0)

  data.contentfulFaqPage.faqs.forEach(faq => {
    if (faq.enabled && !categories.includes(faq.category)) {
      categories.push(faq.category)
    }
  })

  categories.reverse()

  function scrollTo(cat) {
    gsap.to(window, { duration: 1, scrollTo: { y: `#${slugify(cat, { lower: true })}-section`, offsetY: 150 } })
  }

  return (
    <Layout pathName={location.pathname}>
      <Helmet>
        <title>FAQs</title>
      </Helmet>
      <BGImageHero text="FAQ" image={faqPage.backgroundImage.fluid} />

      <Wrapper>
        <StickyNav>
          {categories.map((cat, index) => (
            <button
							className={index === activeCategory ? 'active' : null}
              onClick={() => scrollTo(cat)}
              key={index}
            >
              {cat}
            </button>
          ))}
        </StickyNav>

        <FaqsWrapper>
          {categories.map((cat, index) => (
            <FaqSection
              cat={cat}
              faqPage={faqPage}
              index={index}
              setActiveCategory={setActiveCategory}
              key={index}
            ></FaqSection>
          ))}

          <Callout>
            <p>Not finding what you're looking for?<br></br>Email us at {EMAIL} and we'll be happy to assist you!</p>
            <PrimaryButton as={Link} href={`mailto:${EMAIL}`}>
              Email Us
            </PrimaryButton>
          </Callout>
        </FaqsWrapper>
      </Wrapper>
    </Layout>
  );
}
